import React, { useState, useEffect, useRef } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import {
  CalendarDays,
  Plus,
  Search,
  Trash2,
  ClipboardList,
} from 'lucide-react';
import {
  ProgramacionService,
  type ProgramacionSemanal,
  type PaginatedProgramaciones,
} from '../../services/programacion/programacion.service';
import {
  ProgramacionFormDrawer,
  ProgramacionDetailsDrawer,
} from '../../components/programacion';
import { NotificationToast } from '../../components/ui/NotificationToast';
import { ConfirmationToast } from '../../components/ui/ConfirmationToast';
import { usePermissions } from '../../hooks/usePermissions';
import { parseFechaLocal } from '../../utils/date.utils';

const MESES = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
];

const formatearFechaCorta = (iso: string): string => {
  const d = parseFechaLocal(iso);
  return `${d.getDate()} ${MESES[d.getMonth()]} ${d.getFullYear()}`;
};

const ProgramacionSemanalPage: React.FC = () => {
  const { hasPermission } = usePermissions();
  const canCreate = hasPermission('Programación Semanal', 'crear');
  const canDelete = hasPermission('Programación Semanal', 'eliminar');

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [formDrawerOpen, setFormDrawerOpen] = useState(false);
  const [programacionToEdit, setProgramacionToEdit] = useState<ProgramacionSemanal | null>(null);
  const [detailsDrawerOpen, setDetailsDrawerOpen] = useState(false);
  const [programacionToView, setProgramacionToView] = useState<ProgramacionSemanal | null>(null);

  const [notification, setNotification] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'success' | 'error';
  }>({ isOpen: false, title: '', message: '', type: 'success' });

  const [confirmDelete, setConfirmDelete] = useState<{
    isOpen: boolean;
    programacion: ProgramacionSemanal | null;
    isDeleting: boolean;
  }>({ isOpen: false, programacion: null, isDeleting: false });

  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPage(1);
    }, 500);
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [searchTerm]);

  const { data, isLoading, refetch } = useQuery<PaginatedProgramaciones>({
    queryKey: ['programaciones', page, limit, debouncedSearchTerm],
    queryFn: () => ProgramacionService.list(page, limit, debouncedSearchTerm),
    placeholderData: keepPreviousData,
  });

  const programaciones = Array.isArray(data?.data) ? data.data : [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  const showNotification = (title: string, message: string, type: 'success' | 'error') =>
    setNotification({ isOpen: true, title, message, type });

  const handleCreate = () => {
    setProgramacionToEdit(null);
    setFormDrawerOpen(true);
  };

  const handleEdit = (programacion: ProgramacionSemanal) => {
    setProgramacionToEdit(programacion);
    setDetailsDrawerOpen(false);
    setFormDrawerOpen(true);
  };

  const handleView = (programacion: ProgramacionSemanal) => {
    setProgramacionToView(programacion);
    setDetailsDrawerOpen(true);
  };

  const handleDeleteRequest = (programacion: ProgramacionSemanal) =>
    setConfirmDelete({ isOpen: true, programacion, isDeleting: false });

  const handleDeleteConfirm = async () => {
    if (!confirmDelete.programacion) return;
    setConfirmDelete((prev) => ({ ...prev, isDeleting: true }));
    try {
      await ProgramacionService.delete(confirmDelete.programacion.id);
      setConfirmDelete({ isOpen: false, programacion: null, isDeleting: false });
      showNotification('Programación eliminada', 'La programación semanal se eliminó correctamente.', 'success');
      refetch();
    } catch {
      setConfirmDelete({ isOpen: false, programacion: null, isDeleting: false });
      showNotification('Error', 'No se pudo eliminar la programación.', 'error');
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        {canCreate && (
          <button
            onClick={handleCreate}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-xs font-medium bg-purple-600 text-white hover:bg-purple-700 transition-colors shadow-sm shadow-purple-200"
          >
            <Plus className="w-4 h-4" />
            Nueva Programación
          </button>
        )}
      </div>

      {/* Búsqueda */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Buscar por descripción..."
          className="w-full pl-9 pr-3 py-2.5 text-xs border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500/30 focus:border-purple-500 outline-none bg-white"
        />
      </div>

      {/* Tabla */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-purple-600 border-t-transparent" />
          </div>
        ) : programaciones.length === 0 ? (
          <div className="text-center py-16">
            <ClipboardList className="mx-auto h-12 w-12 text-gray-300 mb-3" />
            <p className="text-xs text-gray-500">
              {debouncedSearchTerm ? 'No se encontraron programaciones con esa búsqueda.' : 'Aún no hay programaciones semanales registradas.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-[10px] font-medium text-gray-500 uppercase">Semana</th>
                  <th className="px-4 py-3 text-left text-[10px] font-medium text-gray-500 uppercase">Descripción</th>
                  <th className="px-4 py-3 text-left text-[10px] font-medium text-gray-500 uppercase">Asignaciones</th>
                  <th className="px-4 py-3 text-left text-[10px] font-medium text-gray-500 uppercase">Creada</th>
                  {canDelete && <th className="px-4 py-3 w-12" />}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {programaciones.map((p) => (
                  <tr
                    key={p.id}
                    onClick={() => handleView(p)}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-3.5 whitespace-nowrap">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center shrink-0">
                          <CalendarDays className="w-4 h-4 text-purple-600" />
                        </div>
                        <span className="text-xs font-medium text-gray-900">
                          {formatearFechaCorta(p.fechaInicio)} — {formatearFechaCorta(p.fechaFin)}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3.5">
                      <span className="text-xs text-gray-600 line-clamp-2 max-w-md">{p.descripcion}</span>
                    </td>
                    <td className="px-4 py-3.5 whitespace-nowrap">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-purple-50 text-purple-700">
                        {p.totalAsignaciones ?? p.asignaciones?.length ?? 0} asignación(es)
                      </span>
                    </td>
                    <td className="px-4 py-3.5 whitespace-nowrap text-xs text-gray-500">
                      {formatearFechaCorta(p.creadoEn)}
                    </td>
                    {canDelete && (
                      <td className="px-4 py-3.5 text-center">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteRequest(p);
                          }}
                          className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 transition-colors"
                          title="Eliminar programación"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Paginación */}
        {!isLoading && programaciones.length > 0 && (
          <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between">
            <span className="text-xs text-gray-500">
              {total} resultado(s) · Página {page} de {totalPages}
            </span>
            <div className="flex items-center gap-2">
              <select
                value={limit}
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  setPage(1);
                }}
                className="px-2 py-1.5 text-xs border border-gray-200 rounded-lg bg-white"
              >
                {[7, 10, 20].map((l) => (
                  <option key={l} value={l}>{l}</option>
                ))}
              </select>
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50"
              >
                Anterior
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50"
              >
                Siguiente
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Drawers */}
      <ProgramacionFormDrawer
        isOpen={formDrawerOpen}
        programacion={programacionToEdit}
        onClose={() => setFormDrawerOpen(false)}
        onSuccess={(message) => {
          showNotification('Operación exitosa', message, 'success');
          refetch();
        }}
        onError={(message) => showNotification('Error', message, 'error')}
      />

      <ProgramacionDetailsDrawer
        isOpen={detailsDrawerOpen}
        programacion={programacionToView}
        onClose={() => setDetailsDrawerOpen(false)}
        onEdit={handleEdit}
      />

      {/* Confirmación de eliminación */}
      {confirmDelete.programacion && (
        <ConfirmationToast
          isOpen={confirmDelete.isOpen}
          title="Eliminar programación"
          message={`¿Seguro que deseas eliminar la programación del ${formatearFechaCorta(confirmDelete.programacion.fechaInicio)}? Esta acción no se puede deshacer.`}
          confirmText="Eliminar"
          type="danger"
          onConfirm={handleDeleteConfirm}
          onCancel={() => setConfirmDelete({ isOpen: false, programacion: null, isDeleting: false })}
          isLoading={confirmDelete.isDeleting}
        />
      )}

      {/* Notificación */}
      <NotificationToast
        isOpen={notification.isOpen}
        title={notification.title}
        message={notification.message}
        type={notification.type}
        onClose={() => setNotification((prev) => ({ ...prev, isOpen: false }))}
        duration={5000}
      />
    </div>
  );
};

export default ProgramacionSemanalPage;
